import { MaterialGroup } from '@cutr/constants/cutlist';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { useLeadDetails } from '@/api/account';
import { api } from '@/api/backend';
import { useCurrentGroupErrors, useCurrentGroupPartErrors } from '@/api/errors';
import {
  setGroupAndParts,
  useCreateGroup,
  useSaveGroupChanges,
} from '@/api/materialsGroup';
import { useActions, useCutlistState } from '@/api/store';
import { useOwnerModalStore } from '@/blocks/CutlistOwnerButton';
import { Button } from '@/primitives/Button';
import { Icon, Save } from '@/primitives/Icons';
import { useCurrentFeatures } from '@/theme';
import { getTrimConfigByType } from '@/utils/features';

import MaterialGroupCard from '../PartTableCommon/MaterialGroupCard';
import { MaterialGroupTypeSelect } from '../PartTableCommon/MaterialGroupTypeSelect';
import { MaterialUpdate } from '../PartTableCommon/types';
import { useProcessingSettingsConfig } from '../PartTableCommon/useProcessingSettingsConfig';
import styles from './MaterialGroupHeader.module.css';
import { MaterialGroupOptions } from './MaterialGroupOptions';

export const MaterialGroupHeader = ({ group }: { group: MaterialGroup }) => {
  if (group.type === 'beams') {
    return <BeamGroupHeader group={group} />;
  }
  return <MaterialSelectionHeader group={group} />;
};

const MaterialSelectionHeader = ({ group }: { group: MaterialGroup }) => {
  const { t } = useTranslation();
  const {
    supportedSheetEdgeTrims,
    defaultSheetEdgeTrimTypeForSheetGroup,
    defaultSheetEdgeTrimTypeForPartGroup,
    visibleGroupTypesOnSingleCore,
  } = useCurrentFeatures();
  const { showGroupTypeSelect } = useProcessingSettingsConfig({ group });

  const { addPart } = useActions();

  const groupPartsCount = useCutlistState(
    (state) => state.parts.filter((p) => p.groupId === group.id).length
  );

  type K = keyof MaterialGroup;
  const onMaterialGroupUpdate: MaterialUpdate = (fields) => {
    let updatedGroup = { ...group, ...fields } as MaterialGroup;

    if (!showGroupTypeSelect) {
      updatedGroup.type = visibleGroupTypesOnSingleCore[0];
    }

    const updatedKeys = Object.keys(fields) as K[];
    if (updatedKeys.includes('core1' as K)) {
      // update trim by group type
      const sheetEdgeTrimConfig = getTrimConfigByType(
        supportedSheetEdgeTrims,
        updatedGroup.type === 'sheets-only'
          ? defaultSheetEdgeTrimTypeForSheetGroup
          : defaultSheetEdgeTrimTypeForPartGroup
      );
      updatedGroup = { ...updatedGroup, sheetEdgeTrimConfig };
    }

    setGroupAndParts(updatedGroup);

    // add part if no parts in group
    if (!groupPartsCount && updatedGroup.type && updatedGroup.core1)
      addPart(updatedGroup);
  };

  const onMaterialRemoval = (field: K) => {
    onMaterialGroupUpdate({
      [field]: null,
      automaticSheetSizeMaterials: [],
      sheetSizeSelection: 'manual',
    });
  };

  return (
    <>
      <div className={styles.materialGroupHeader}>
        <h3>{t('agent.quoteFlow.materialSettings')}</h3>
        <SaveMaterialGroupButton group={group} />
      </div>
      <div className={styles.materialGroup}>
        <div className={styles.materialsList}>
          <MaterialGroupCard
            i18nKey="core"
            field="core1"
            group={group}
            onUpdateMaterial={onMaterialGroupUpdate}
            onRemoveMaterial={onMaterialRemoval}
          />
        </div>

        {showGroupTypeSelect && <MaterialGroupTypeSelect group={group} />}

        <MaterialGroupOptions group={group} />
      </div>
    </>
  );
};

const BeamGroupHeader = ({ group }: { group: MaterialGroup }) => {
  const { t } = useTranslation();

  React.useEffect(() => {
    if (!group.beamMaterialId) {
      // If no beam material is set, we set the first available material
      api.beamMaterials({ limit: 1 }).then((materials) => {
        if (materials.length > 0) {
          const firstMaterial = materials[0];
          setGroupAndParts({
            ...group,
            name: firstMaterial.variationGroup?.name || firstMaterial.name,
            beamMaterialId: firstMaterial.id,
          });
        }
      });
    }
  }, [group.beamMaterialId, group.id, group]);

  return (
    <>
      <div className={styles.beamGroupHeader}>
        <h3>{t('agent.quoteFlow.materialSettings')}</h3>
        <SaveMaterialGroupButton group={group} />
      </div>
      <div className={styles.materialGroup}>
        <div className={styles.beamMaterialCard}>
          <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
            <rect width="32" height="32" fill="url(#pattern0_52_12872)" />
            <defs>
              <pattern
                id="pattern0_52_12872"
                patternContentUnits="objectBoundingBox"
                width="1"
                height="1"
              >
                <use
                  xlinkHref="#image0_52_12873"
                  transform="scale(0.00826446)"
                />
              </pattern>
              <image
                id="image0_52_12873"
                width="121"
                height="121"
                preserveAspectRatio="none"
                xlinkHref={'/beams_thumbnail.png'}
              />
            </defs>
          </svg>

          <div>{group.name}</div>
        </div>
      </div>
    </>
  );
};

const SaveMaterialGroupButton = ({ group }: { group: MaterialGroup }) => {
  const { t } = useTranslation();
  const { email } = useLeadDetails();
  const { openModal: openOwnerModal } = useOwnerModalStore();

  const currentGroupPartErrors = useCurrentGroupPartErrors();
  const currentGroupErrors = useCurrentGroupErrors();

  const [createMaterialGroup, canCreateMaterialGroup] = useCreateGroup({
    group,
  });
  const [saveMaterialGroup, canSaveMaterialGroup] = useSaveGroupChanges({
    group,
  });

  const hasErrors =
    currentGroupPartErrors.length > 0 || currentGroupErrors.length > 0;

  const hasMaterial =
    group.type === 'beams'
      ? Boolean(group.beamMaterialId)
      : Boolean(group.core1);

  let addToOrderTooltip: undefined | string = undefined;
  if (!hasMaterial) {
    addToOrderTooltip = t('agent.tooltip.noPartCreated');
  }
  if (hasErrors) {
    addToOrderTooltip = t('agent.tooltip.addMaterialErrors');
  }

  const openOwnerModalWithCallback = (onSuccess: () => void) => {
    openOwnerModal(
      t('agent.setCutlistOwnership.ownerRequiredForSaveMaterialGroup'),
      onSuccess
    );
  };

  if (group.ulid) {
    return (
      <Button
        className="smaller pill"
        disabled={hasErrors || !canSaveMaterialGroup}
        icon={<Icon icon={<Save />} />}
        onClick={() => {
          if (email) {
            saveMaterialGroup();
          } else {
            openOwnerModalWithCallback(saveMaterialGroup);
          }
        }}
        style={{
          // @ts-ignore
          '--button-bkg': 'var(--agent-accent)',
        }}
      >
        <strong>{t('agent.saveChanges')}</strong>
      </Button>
    );
  }

  return (
    <Button
      className="smaller pill tooltipLeft"
      disabled={hasErrors || !canCreateMaterialGroup}
      tooltip={addToOrderTooltip}
      icon={<Icon icon={<Save />} />}
      onClick={() => {
        if (email) {
          createMaterialGroup();
        } else {
          openOwnerModalWithCallback(createMaterialGroup);
        }
      }}
      style={{
        // @ts-ignore
        '--button-bkg': 'var(--agent-accent)',
      }}
    >
      <strong>{t('agent.saveChanges')}</strong>
    </Button>
  );
};
